import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Paper,
  TablePagination,
  alpha,
  useTheme,
  Chip,
  Stack,
  CircularProgress,
  Container,
  Card
} from "@mui/material";

import { pickTransaction } from "../api/leads";
import { fetchAllPendingPayments } from "../api/payments";
import CommonDialog from "../components/commonDialog";
import CustomSnackbarAlert from "../components/customAlert";
import { useNavigate } from "react-router-dom";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Payments as PaymentsIcon, ContactPhone as PhoneIcon, Mail as MailIcon } from "@mui/icons-material";

export default function PendingPayments() {
  const theme = useTheme();
  const [paymentsData, setPaymentsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showdialog, setShowDialog] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogContent, setDialogContent] = useState("");
  const [selectedEntry, setSelectedEntry] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
  });

  const navigate = useNavigate();

  const [sortConfig, setSortConfig] = useState({
    key: "date",
    direction: "desc",
  });

  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [showAlert, setShowAlet] = useState(false);

  const sortedPayments = paymentsData?.transactions
    ? [...paymentsData.transactions].sort((a, b) => {
        if (!sortConfig.key) return 0;
        const aValue = a?.[sortConfig.key] ?? "";
        const bValue = b?.[sortConfig.key] ?? "";

        if (typeof aValue === "number" && typeof bValue === "number") {
          return sortConfig.direction === "asc"
            ? aValue - bValue
            : bValue - aValue;
        }

        const aStr = String(aValue).toLowerCase();
        const bStr = String(bValue).toLowerCase();

        if (aStr < bStr) return sortConfig.direction === "asc" ? -1 : 1;
        if (aStr > bStr) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      })
    : [];

  const handleSort = (key) => {
    setSortConfig((prevConfig) => {
      const isSameKey = prevConfig.key === key;
      const newDirection =
        isSameKey && prevConfig.direction === "asc" ? "desc" : "asc";
      return { key, direction: newDirection };
    });
  };

  const loadPayments = async () => {
    setLoading(true);
    try {
      const result = await fetchAllPendingPayments({
        page: page + 1,
        limit: rowsPerPage,
      });
      if (result.status) {
        setPaymentsData(result.data);
        setPagination(result.data.pagination);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const openDialog = (transactionId) => {
    setSelectedEntry(transactionId);
    setDialogTitle("Pick Lead");
    setDialogContent("Are you sure you want to pick this lead and assign it to yourself?");
    setShowDialog(true);
  };

  const handleCancel = () => {
    setShowDialog(false);
  };

  const handleCloseSnackbar = () => {
    setShowAlet(false);
  };

  const handleAgree = async () => {
    const data = await pickTransaction(selectedEntry);
    if (data.status) {
      loadPayments();
      setAlertMessage("Lead picked successfully");
      setAlertSeverity("success");
      setShowAlet(true);
      navigate(`/payment-lead?id=${data.data._id}`);
    }
    setShowDialog(false);
  };

  useEffect(() => {
    loadPayments();
  }, [page, rowsPerPage]);

  if (loading && !paymentsData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
        <Box>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="h4" sx={{ fontWeight: 900 }}>
              Pending Payments
            </Typography>
            <Chip 
              label={`${pagination.totalCount} Leads`} 
              color="primary" 
              sx={{ fontWeight: 800, borderRadius: 2 }} 
            />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Pick and manage new payment arrivals
          </Typography>
        </Box>
      </Box>

      {error ? (
        <Typography color="error" textAlign="center">Error: {error}</Typography>
      ) : (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 350px)' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {[
                    { key: "name", label: "Customer" },
                    { key: "email", label: "Email" },
                    { key: "phone", label: "Phone" },
                    { key: "date", label: "Payment Date" },
                    { key: "amount", label: "Amount" },
                  ].map((column) => (
                    <TableCell
                      key={column.key}
                      onClick={() => handleSort(column.key)}
                      sx={{
                        fontWeight: 800,
                        backgroundColor: 'grey.50',
                        color: 'text.secondary',
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        cursor: "pointer",
                        py: 2,
                        "&:hover": { color: 'primary.main' },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {column.label}
                        {sortConfig.key === column.key && (
                          sortConfig.direction === "asc" ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />
                        )}
                      </Box>
                    </TableCell>
                  ))}
                  <TableCell sx={{ fontWeight: 800, backgroundColor: 'grey.50', fontSize: '0.75rem', textTransform: 'uppercase' }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {sortedPayments.map((payment, index) => (
                  <TableRow key={index} hover sx={{ '&:last-child td': { border: 0 } }}>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{payment.name || "N/A"}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                        <MailIcon fontSize="inherit" />
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{payment.email}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                        <PhoneIcon fontSize="inherit" />
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{payment.phone}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {new Date(payment.date).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        {new Date(payment.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: 'primary.main' }}>
                        ₹{payment.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        size="small"
                        color="secondary"
                        sx={{ 
                          borderRadius: 2, 
                          fontWeight: 800, 
                          px: 2,
                          boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.2)}`,
                          '&:hover': { boxShadow: `0 6px 16px ${alpha(theme.palette.secondary.main, 0.3)}` }
                        }}
                        onClick={() => openDialog(payment._id)}
                      >
                        Pick Lead
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            component="div"
            count={pagination.totalCount}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 20, 50]}
            sx={{ borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}
          />
        </Card>
      )}

      <CommonDialog
        open={showdialog}
        title={dialogTitle}
        content={dialogContent}
        onCancel={handleCancel}
        onAgree={handleAgree}
      />
      <CustomSnackbarAlert
        message={alertMessage}
        severity={alertSeverity}
        open={showAlert}
        onClose={handleCloseSnackbar}
      />
    </Container>
  );
}

